/**
 * Client-Side Feature Flags
 *
 * ⚠️ SINGLE SOURCE OF TRUTH: server/config/featureFlags.js
 *
 * Architecture:
 * 1. initializeFlags() is called in index.js BEFORE React renders
 * 2. Fetches all flags from /api/features (2 second timeout)
 * 3. CRITICAL_DEFAULTS are kept as safety net if fetch fails
 * 4. Server values override defaults when fetch succeeds
 *
 * This ensures:
 * - No race condition (flags ready before any component renders)
 * - Graceful degradation (critical UI features work even if fetch fails)
 * - Single source of truth (server defines all flags and their defaults)
 *
 * Usage:
 *   import { useFeatureFlag, isFeatureEnabled } from './config/featureFlags';
 *
 *   // In a component (reactive):
 *   const streamingEnabled = useFeatureFlag('streaming_text_updates');
 *
 *   // Outside React (static check):
 *   if (isFeatureEnabled('streaming_text_updates')) { ... }
 */

import { useState, useEffect } from "react";
import { API_BASE_URL } from "./api";

// CRITICAL UI DEFAULTS - Safety net if server fetch fails
// These are features that MUST be on or the UI breaks
// Server values override these once fetched successfully
const CRITICAL_DEFAULTS = {
  streaming_text_updates: true, // Must be ON or UI breaks
  automations: true, // Show automations button
  affiliates: true, // Show affiliate button
  mcp_admin: true, // Admin-gated in UI anyway
  custom_dashboards: true, // Admin-gated in UI anyway
  image_upload: true, // Drag-and-drop images into chat
  codemirror_editor: true, // CodeMirror for MCP code editing
  message_queueing: true, // Send messages while agent is working
  c3_workspace: true, // VFS file browser toggle in sidebar
  liquid_glass: true, // Liquid Glass design system (dark mode polish)
};

// Global flag cache - populated by initializeFlags() in index.js BEFORE React renders
// Starts with CRITICAL_DEFAULTS as safety net, server values override
let flagCache = { ...CRITICAL_DEFAULTS };
let initialized = false;
let initPromise = null;

export const COMMAND_CENTER_PREVIEW_FLAGS = [
  "command_center_ui",
  "command_center_chat",
  "command_center_execution",
  "command_center_objectives",
  "command_center_approvals",
  "command_center_destinations",
  "command_center_attention",
  "command_center_reusable_results",
  "command_center_mobile",
];

/**
 * Enable the authenticated Command Center in this browser only. This is used
 * by the staging preview URL and never mutates server-side/shared flags.
 */
export function enableCommandCenterPreview() {
  COMMAND_CENTER_PREVIEW_FLAGS.forEach((flagName) => {
    localStorage.setItem(`ff_${flagName}`, "true");
  });
}

export function clearCommandCenterPreview() {
  COMMAND_CENTER_PREVIEW_FLAGS.forEach((flagName) => {
    localStorage.removeItem(`ff_${flagName}`);
  });
}

/**
 * Fetch feature flags from server (the ONLY source of truth)
 * Times out after 2 seconds to avoid blocking render on slow connections
 * @returns {Promise<object>} Flag values
 */
async function fetchFlags() {
  try {
    // 2 second timeout - don't block render too long on cold starts
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);

    const response = await fetch(`${API_BASE_URL}/api/features`, {
      credentials: "include",
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error("[FeatureFlags] Server returned", response.status);
      return {};
    }

    const data = await response.json();
    // Server returns { success: true, features: { flagName: boolean, ... } }
    if (data.success && data.features) {
      return data.features;
    }

    console.error("[FeatureFlags] Invalid server response", data);
    return {};
  } catch (error) {
    if (error.name === "AbortError") {
      console.warn("[FeatureFlags] Fetch timed out after 2s, using defaults");
    } else {
      console.error(
        "[FeatureFlags] Failed to fetch from server:",
        error.message,
      );
    }
    return {};
  }
}

/**
 * Initialize feature flags (call once on app start)
 * MUST complete before using any flags
 * @returns {Promise<void>}
 */
export async function initializeFlags() {
  if (initialized) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const flags = await fetchFlags();
      // Merge with critical defaults - server values take precedence
      // If fetch returned empty object, we still have safety defaults
      flagCache = { ...CRITICAL_DEFAULTS, ...flags };
      initialized = true;
      console.log(
        "[FeatureFlags] Loaded from server:",
        Object.keys(flagCache).length,
        "flags",
      );
    } catch (error) {
      console.error("[FeatureFlags] Initialization failed:", error);
      initialized = true; // Mark as initialized to prevent infinite retries
    }
  })();

  return initPromise;
}

/**
 * Check if a feature flag is enabled (synchronous)
 * Returns false if flag unknown (safe default)
 *
 * @param {string} flagName - Name of the feature flag
 * @returns {boolean} Whether the flag is enabled
 */
export function isFeatureEnabled(flagName) {
  // Check localStorage override first (for development/testing)
  const override = localStorage.getItem(`ff_${flagName}`);
  if (override !== null) {
    return override === "true";
  }

  if (flagCache[flagName] !== undefined) {
    return flagCache[flagName];
  }

  // Flag not found - log warning and return false (safe default)
  if (initialized) {
    console.warn("[FeatureFlags] Unknown flag:", flagName);
  }
  return false;
}

/**
 * React hook for feature flags (reactive)
 *
 * @param {string} flagName - Name of the feature flag
 * @returns {boolean} Whether the flag is enabled
 */
export function useFeatureFlag(flagName) {
  // Flags are initialized in index.js BEFORE React renders, so cache is always populated
  const [enabled, setEnabled] = useState(() => isFeatureEnabled(flagName));

  useEffect(() => {
    // Listen for localStorage changes (for dev tools)
    const handleStorage = (e) => {
      if (e.key === `ff_${flagName}`) {
        setEnabled(e.newValue === "true");
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [flagName]);

  return enabled;
}

/**
 * Set a feature flag override (for development/testing only)
 * Persists in localStorage, does NOT affect server
 *
 * @param {string} flagName - Name of the feature flag
 * @param {boolean} enabled - New value
 */
export function setFlagOverride(flagName, enabled) {
  localStorage.setItem(`ff_${flagName}`, String(enabled));

  // Dispatch storage event for other tabs/hooks
  window.dispatchEvent(
    new StorageEvent("storage", {
      key: `ff_${flagName}`,
      newValue: String(enabled),
    }),
  );

  console.log(`[FeatureFlags] Local override set: ${flagName} = ${enabled}`);
}

/**
 * Clear a feature flag override
 *
 * @param {string} flagName - Name of the feature flag
 */
export function clearFlagOverride(flagName) {
  localStorage.removeItem(`ff_${flagName}`);
  console.log(`[FeatureFlags] Local override cleared: ${flagName}`);
}

/**
 * Get all current flag values
 * @returns {object} All flag values from server
 */
export function getAllFlags() {
  return { ...flagCache };
}

/**
 * Check if flags have been loaded from server
 * @returns {boolean}
 */
export function isInitialized() {
  return initialized;
}

// Flag name constants - keep in sync with server/config/featureFlags.js
// These are just string constants for convenience, not defaults
export const FLAGS = {
  STREAMING_TEXT_UPDATES: "streaming_text_updates",
  HARRY_ESCALATION: "harry_escalation",
  OLLIE_VERIFICATION: "ollie_verification",
  SAL_ORCHESTRATOR: "sal_orchestrator",
  BLIND_SYNTHESIZER: "blind_synthesizer",
  AUTOMATIONS: "automations",
  SEMANTIC_SEARCH: "semantic_search",
  STICKY_TOOL_LOADOUT: "sticky_tool_loadout",
  FAST_FAIL_MODE: "fast_fail_mode",
  FAIL_SMART_MODE: "fail_smart_mode",
  PLAYWRIGHT_BROWSER: "playwright_browser",
  SEQUENTIAL_TOOL_CALLS: "sequential_tool_calls",
  CLAUDE_WEB_SEARCH: "claude_web_search",
  SHARED_BRAIN_SYNC: "shared_brain_sync",
  MAKO_BRIDGE: "mako_bridge",
  HARRY_TRIGGERS: "harry_triggers",
  HARRY_TASK_IDENTITY: "harry_task_identity",
  CUSTOM_MCP_OAUTH_REFRESH: "custom_mcp_oauth_refresh",
  CUSTOM_MCP_ROTATE_PROBE: "custom_mcp_rotate_probe",
  ACTIVATION_COACH: "activation_coach",
  SKILLS_LIBRARY: "skills_library",
  CUSTOM_SKILL_BUILDER: "custom_skill_builder",
  SKILL_FROM_RECORDING: "skill_from_recording",
  SKILL_TEAM_SHARING: "skill_team_sharing",
  COMMAND_CENTER_UI: "command_center_ui",
  COMMAND_CENTER_CHAT: "command_center_chat",
  COMMAND_CENTER_EXECUTION: "command_center_execution",
  COMMAND_CENTER_OBJECTIVES: "command_center_objectives",
  COMMAND_CENTER_APPROVALS: "command_center_approvals",
  COMMAND_CENTER_DESTINATIONS: "command_center_destinations",
  COMMAND_CENTER_ATTENTION: "command_center_attention",
  COMMAND_CENTER_REUSABLE_RESULTS: "command_center_reusable_results",
  COMMAND_CENTER_MOBILE: "command_center_mobile",
  ZEN_MODE: "zen_mode",
  MCP_ADMIN: "mcp_admin",
  CUSTOM_DASHBOARDS: "custom_dashboards",
  CUSTOM_DASHBOARDS_V2: "custom_dashboards_v2",
  AUTO_DEBUG_SWEEP: "auto_debug_sweep",
  GITHUB_BACKUP: "github_backup",
  ZERO_TOOL_CLAIM_GUARD: "zero_tool_claim_guard",
  AUTOMATION_UPDATE_TOOL: "automation_update_tool",
  AUTOMATION_DELETE_TOOL: "automation_delete_tool",
  SYSTEM_NOTICE_HISTORY_SCRUB: "system_notice_history_scrub",
  CLAUDE_OVERFLOW_FAILOVER: "claude_overflow_failover",
  GAMMA_FREE_SAMPLES: "gamma_free_samples",
  RECEIPTS_BRIEFING: "receipts_briefing",
  ACTION_INTENT_BRIEFING: "action_intent_briefing",
  IMPLIED_TOOL_STEER: "implied_tool_steer",
  STREAM_CONTAINMENT: "stream_containment",
  LATE_BIND_TOOLS: "late_bind_tools",
  TURN_ZERO_BRIEFING: "turn_zero_briefing",
  SMART_CONTEXT_TRIM: "smart_context_trim",
  SETTINGS_GLOSSARY: "settings_glossary",
  PWA_INSTALL_NUDGE: "pwa_install_nudge",
  MAKO_TWO_WAY_SYNC: "mako_two_way_sync",
  PROACTIVE_TELEGRAM_DELIVERY: "proactive_telegram_delivery",
  HARRY_OVERFLOW_ON_SESSION_LIMIT: "harry_overflow_on_session_limit",
  HARRY_BURY_CREDENTIALLESS_WORKERS: "harry_bury_credentialless_workers",
  HARRY_FLEET_BUILD_HEAL: "harry_fleet_build_heal",
  TEAM_WORKSPACES: "team_workspaces",
  OWNER_CREDS_SHARED_DASHBOARDS: "owner_creds_shared_dashboards",
  BEHAVIOR_EVENTS: "behavior_events",
  APPROVAL_GATES: "approval_gates",
  BYOK_ONLY: "byok_only",
  INTERNET_RESEARCH: "internet_research",
  AFFILIATES: "affiliates",
  IMAGE_UPLOAD: "image_upload",
  CODEMIRROR_EDITOR: "codemirror_editor",
  MESSAGE_QUEUEING: "message_queueing",
  C3_WORKSPACE: "c3_workspace",
  LIQUID_GLASS: "liquid_glass",
  JQ_FILTER: "jq_filter",
  CANVA_OAUTH: "canva_oauth",
  SELF_BUILD_MCP: "self_build_mcp",
  CUSTOM_TOOLS: "custom_tools",
  MODEL_FALLBACKS: "model_fallbacks",
  TRAINING_DATA_CAPTURE: "training_data_capture",
  COACH_MODE: "coach_mode",
  FEEDBACK_STEER: "feedback_steer",
  TRANSCRIPTION_LIMITS: "transcription_limits",
  SUPPORT_LOOP: "support_loop",
  LANGUAGE_MIRRORING: "language_mirroring",
  KEY_SCRUBBER: "key_scrubber",
  COACH_SLM: "coach_slm",
  HARRY_QUEUE_REAPER: "harry_queue_reaper",
  RECEIPTS_FOOTER: "receipts_footer",
  HONEST_CORRECTION_FLOOR: "honest_correction_floor",
  COACH_DUELS_SHADOW: "coach_duels_shadow",
  COACH_DUEL_JUDGE: "coach_duel_judge",
  PROD_CANARIES: "prod_canaries",
};

// Export default object for convenience
export default {
  isFeatureEnabled,
  useFeatureFlag,
  setFlagOverride,
  clearFlagOverride,
  getAllFlags,
  initializeFlags,
  isInitialized,
  FLAGS,
};
